// Propagate a person-level edit across all crew rows that share the
// same person identity. Identity is _personId (a stable client-side
// uuid assigned at row creation) when present, falling back to
// case-insensitive email otherwise.
//
// Background: `crew_members` is per-day — one row per (briefing,
// person, event_date). The producer editor groups rows into one card
// per person, so editing `function` on a card has to apply to every
// row in that group; pre-fix, only the active day's row was updated
// and the partition step then saw the other days as "different
// person".
//
// Why _personId, not just email: typing the email character-by-character
// otherwise splits a half-typed group on every keystroke (the first
// row gets the new email, the rest stay blank, the producer sees the
// card collapse + a new empty card appear). The stable identity rides
// over email edits, joins, and renames.
//
// Person-level fields (name, email, phone, function, color, hasColor)
// fan out to every other row in the group. Day-level fields
// (event_date, arrival_time) are NOT person-level and must not be
// passed here.
//
// When `email` is in the patch, `isExistingUser` is reset to
// `undefined` on every affected row so the post-blur lookup runs
// fresh against the new email.

export type PersonRow = {
  id?: string;
  // Stable client-side identity that survives email edits. Assigned
  // at row creation (addCrew / load) and propagated across the
  // person's day-rows via this helper. Falls back to lower(email)
  // when not set — covers any code path that constructs a row without
  // a _personId and any test that doesn't bother assigning one.
  _personId?: string;
  name: string;
  email: string;
  phone?: string | null;
  function: string;
  color?: string | null;
  hasColor?: boolean;
  isExistingUser?: boolean;
};

export type PersonFieldPatch = Partial<{
  name: string;
  email: string;
  phone: string | null;
  function: string;
  color: string | null;
  hasColor: boolean;
}>;

const norm = (s: string | null | undefined): string => (s ?? "").trim().toLowerCase();

// Match key for "is this the same person?" — _personId wins because
// it's stable across email edits. Email is the legacy identity used
// when _personId hasn't been assigned yet (e.g. tests, code paths
// that pre-date the field).
const matchKeyOf = (row: PersonRow): string => {
  if (row._personId) return `__pid-${row._personId}__`;
  return norm(row.email);
};

export function applyPersonFieldAcrossDays<T extends PersonRow>(
  crew: T[],
  index: number,
  patch: PersonFieldPatch,
): T[] {
  if (index < 0 || index >= crew.length) return crew;
  const edited = crew[index];
  if (!edited) return crew;
  const editedKey = matchKeyOf(edited);
  const emailChanging = "email" in patch;

  return crew.map((row, i) => {
    if (i === index) {
      return {
        ...row,
        ...patch,
        ...(emailChanging ? { isExistingUser: undefined } : {}),
      };
    }
    const rowKey = matchKeyOf(row);
    // Empty match key on either side is not a valid identity — never
    // propagate from / to rows that lack both _personId and email.
    if (!editedKey || !rowKey || rowKey !== editedKey) return row;
    return {
      ...row,
      ...patch,
      ...(emailChanging ? { isExistingUser: undefined } : {}),
    };
  });
}
